import type {
  PageDetail as ApiDetail,
  PageLimits as ApiLimits,
  PageListRow,
} from '@/api/generated';
import type { StatusTone } from '@/components/ui/StatusChip';
import { formatCount } from '@/lib/numbers';

/**
 * A page as the screens read it (design.md "Domain model"): the backend's
 * shapes in camel case, mapped in one place so a backend rename is one edit.
 */

export const LIFECYCLES = ['DRAFT', 'PUBLISHED', 'INACTIVE', 'ARCHIVED'] as const;
export type Lifecycle = (typeof LIFECYCLES)[number];

/** A card on the list. */
export interface PageRow {
  id: string;
  title: string;
  summary: string | null;
  lifecycle: Lifecycle;
  inArc: boolean;
  views: number;
  publishedAt: string | null;
  updatedAt: string;
}

/** Every length and count the editor bounds, from the reply's `limits` (P3, I5). */
export interface PageLimits {
  titleMaxLength: number;
  summaryMaxLength: number;
  altMaxLength: number;
  maxLinkedDocuments: number;
  richTextMaxLength: number;
}

export type LinkType = 'DOCUMENT' | 'CAMPAIGN' | 'PETITION';

/** One Linked Content entry; campaigns and petitions are never created here. */
export interface LinkRow {
  resourceType: LinkType;
  resourceId: string;
  /** The target's title, or null when its module no longer returns it. */
  title: string | null;
  /** False when the detail said so, or the target is not published (P15). */
  available: boolean;
}

export interface HeaderImage {
  key: string;
  /** An address to show it by; null when the reply gave none. */
  url: string | null;
  /** Empty means the app uses the title (P5). */
  alt: string;
}

/** The editor's form values; the content is held outside the form (PNF2). */
export interface PageForm {
  title: string;
  summary: string;
  headerImage: HeaderImage | null;
  links: LinkRow[];
}

/**
 * One page as the editor and Preview read it. The views figure is the
 * list's alone (B11, P9): one page's reply carries none (B12).
 */
export interface PageDetail extends Omit<PageRow, 'views'> {
  /** The stored TipTap document, with image keys; null when there is none. */
  content: Record<string, unknown> | null;
  /** A signed address per image key the content names. */
  images: Record<string, string>;
  headerImage: HeaderImage | null;
  /** Sent back as `expected_version` on a save (I6). */
  version: number;
  links: LinkRow[];
  limits: PageLimits;
}

/** The lifecycle words (I1); ARCHIVED reads Removed, as the ARC says (P37). */
const STATE_WORDS: Record<Lifecycle, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  INACTIVE: 'Unpublished',
  ARCHIVED: 'Removed',
};

const STATE_TONES: Record<Lifecycle, StatusTone> = {
  DRAFT: 'neutral',
  PUBLISHED: 'success',
  INACTIVE: 'neutral',
  ARCHIVED: 'neutral',
};

export function stateWord(lifecycle: Lifecycle): string {
  return STATE_WORDS[lifecycle];
}

export function stateTone(lifecycle: Lifecycle): StatusTone {
  return STATE_TONES[lifecycle];
}

export function placementWord(inArc: boolean): string {
  return inArc ? 'In ARC' : 'Not in ARC';
}

/**
 * The views over the last 30 days, the backend's default range (P9, P18,
 * PB12), written by `formatCount` and in the singular for one: a draft has
 * never been seen in the app, so it shows none.
 */
export function pageViewsLabel(views: number, isDraft: boolean, locale?: string): string {
  if (isDraft) return '-- views';
  if (views === 1) return '1 view (30 days)';
  return `${formatCount(views, locale)} views (30 days)`;
}

export function toPageRow(api: PageListRow): PageRow {
  return {
    id: api.id,
    title: api.title,
    summary: api.summary ?? null,
    lifecycle: api.lifecycle as Lifecycle,
    inArc: api.in_arc,
    views: api.views,
    publishedAt: api.published_at ?? null,
    updatedAt: api.updated_at,
  };
}

export function toPageLimits(api: ApiLimits): PageLimits {
  return {
    titleMaxLength: api.title_max_length,
    summaryMaxLength: api.summary_max_length,
    altMaxLength: api.header_image_alt_max_length,
    maxLinkedDocuments: api.max_linked_documents,
    richTextMaxLength: api.rich_text.max_length,
  };
}

function toHeaderImage(api: ApiDetail): HeaderImage | null {
  return api.header_image
    ? {
        key: api.header_image.key,
        url: api.header_image.url ?? null,
        alt: api.header_image.alt ?? '',
      }
    : null;
}

/** An unpublished document is unavailable too: the app leaves it out (P15). */
function toLinks(api: ApiDetail): LinkRow[] {
  return api.links.map((link) => ({
    resourceType: link.resource_type as LinkType,
    resourceId: link.resource_id,
    title: link.title ?? null,
    available: link.available && link.is_published !== false,
  }));
}

/** The detail reply, read from its own fields; it has no list row inside. */
export function toPageDetail(api: ApiDetail): PageDetail {
  return {
    id: api.id,
    title: api.title,
    summary: api.summary ?? null,
    lifecycle: api.lifecycle as Lifecycle,
    inArc: api.in_arc,
    publishedAt: api.published_at ?? null,
    updatedAt: api.updated_at,
    version: api.version,
    content: api.content ?? null,
    images: api.images ?? {},
    headerImage: toHeaderImage(api),
    links: toLinks(api),
    limits: toPageLimits(api.limits),
  };
}
